/**
 * @fileoverview Storage-adapter factories with a uniform synchronous
 * contract { get, set, subscribe } so the `createAppI18n` factory can
 * resolve initial language at boot time without awaiting I/O.
 *
 * Three deployment contexts:
 *
 *   - createWebStorageAdapter(storageService)
 *       Wraps @puffin-core/storage StorageService. Cross-tab sync is
 *       handled by StorageService's window 'storage' event listener;
 *       this adapter just forwards subscribe to it.
 *
 *   - createNativeStorageAdapter({asyncStorage, initialSnapshot, onWriteError})
 *       Wraps @react-native-async-storage/async-storage (or any
 *       getItem/setItem/removeItem-shaped service). Sync reads come
 *       from an in-memory snapshot that the caller hydrates BEFORE
 *       app render. Writes update the snapshot synchronously AND
 *       fire-and-forget the async persist call. AsyncStorage rejections
 *       are surfaced via the optional onWriteError callback so a
 *       transient disk failure does not break i18n.
 *
 *   - createMemoryAdapter({initialValues})
 *       Pure in-memory map. Used by tests + as an isomorphic fallback
 *       in environments without StorageService (e.g. SSR).
 *
 * @module @puffin-core/i18n-bootstrap/storageAdapters
 */

/**
 * Build an adapter backed by a synchronous in-memory Map.
 *
 * @param {Object} [options]
 * @param {Object<string,string>} [options.initialValues={}] - Seed entries.
 * @returns {{
 *   get: (key: string) => string|null,
 *   set: (key: string, value: string|null) => void,
 *   subscribe: (key: string, cb: (newValue: string|null) => void) => () => void,
 * }}
 */
export function createMemoryAdapter({ initialValues = {} } = {}) {
  const store = new Map(Object.entries(initialValues));
  const subscribers = new Map();

  return {
    get(key) {
      return store.has(key) ? store.get(key) : null;
    },
    set(key, value) {
      if (value === null || value === undefined) {
        store.delete(key);
      } else {
        store.set(key, value);
      }
      const subs = subscribers.get(key);
      if (subs) {
        for (const cb of subs) {
          cb(value === undefined ? null : value);
        }
      }
    },
    subscribe(key, cb) {
      if (!subscribers.has(key)) {
        subscribers.set(key, new Set());
      }
      subscribers.get(key).add(cb);
      return () => {
        const subs = subscribers.get(key);
        if (subs) {
          subs.delete(cb);
          if (subs.size === 0) {
            // Drop the empty Set so subsequent calls observe the
            // never-subscribed branch (memory hygiene + branch parity).
            subscribers.delete(key);
          }
        }
      };
    },
  };
}

/**
 * Build an adapter backed by a @puffin-core/storage StorageService
 * instance. All three operations forward to the underlying service.
 *
 * @param {Object} storageService - @puffin-core/storage StorageService
 *   instance (typically `StorageService.getInstance()`).
 * @returns {{
 *   get: (key: string) => string|null,
 *   set: (key: string, value: string|null) => void,
 *   subscribe: (key: string, cb: (newValue: string|null) => void) => () => void,
 * }}
 */
export function createWebStorageAdapter(storageService) {
  if (!storageService) {
    throw new Error('createWebStorageAdapter: storageService is required');
  }
  return {
    get(key) {
      const value = storageService.get(key);
      return value === undefined ? null : value;
    },
    set(key, value) {
      storageService.set(key, value);
    },
    subscribe(key, cb) {
      return storageService.subscribe(key, (newValue) => {
        cb(newValue === undefined ? null : newValue);
      });
    },
  };
}

/**
 * Build an adapter for React Native AsyncStorage. Sync reads come from
 * an in-memory snapshot (caller must hydrate via AsyncStorage.getItem
 * BEFORE the i18n factory is invoked). Writes update the snapshot
 * synchronously AND fire-and-forget the persistent write.
 *
 * @param {Object} options
 * @param {Object} options.asyncStorage - AsyncStorage-shaped service
 *   exposing `getItem(key) → Promise<string|null>`,
 *   `setItem(key, value) → Promise<void>`, and
 *   `removeItem(key) → Promise<void>`.
 * @param {Object<string,string>} [options.initialSnapshot={}] - Hydrated
 *   key→value map; populates the in-memory cache for sync reads.
 * @param {(error: Error, key: string, value: string|null) => void}
 *   [options.onWriteError] - Optional callback invoked when the
 *   async persist fails. Default: silent (no throw on the boot path).
 * @returns {{
 *   get: (key: string) => string|null,
 *   set: (key: string, value: string|null) => void,
 *   subscribe: (key: string, cb: (newValue: string|null) => void) => () => void,
 * }}
 */
export function createNativeStorageAdapter({
  asyncStorage,
  initialSnapshot = {},
  onWriteError,
} = {}) {
  if (!asyncStorage) {
    throw new Error('createNativeStorageAdapter: asyncStorage is required');
  }

  const memory = createMemoryAdapter({ initialValues: initialSnapshot });

  return {
    get(key) {
      return memory.get(key);
    },
    set(key, value) {
      memory.set(key, value);
      const persistPromise =
        value === null || value === undefined
          ? asyncStorage.removeItem(key)
          : asyncStorage.setItem(key, value);
      // Fire-and-forget: never let a write failure break the i18n flow.
      Promise.resolve(persistPromise).catch((error) => {
        if (typeof onWriteError === 'function') {
          onWriteError(error, key, value);
        }
      });
    },
    subscribe(key, cb) {
      return memory.subscribe(key, cb);
    },
  };
}
